import type { ProductSecondaryIdentifier } from '@xbcb/api-gateway-client';
import { pick } from 'lodash';
import { transformLines } from './workOrder/usConsumptionEntry';

const PRODUCT_US_ENTRY_LINE_FIELDS: string[] = [
  'adCase',
  'adCaseNumber',
  'adDisclaimerCode',
  'adPaymentMethod',
  'adValue',
  'aluminumExclusionNumber',
  'aluminumRequirements',
  'cvCase',
  'cvCaseNumber',
  'cvDisclaimerCode',
  'cvPaymentMethod',
  'cvValue',
  'description',
  'doesNotContainAluminum',
  'doesNotContainSteel',
  'feeOptions',
  'licenseCode',
  'licenseNumber',
  'manufacturer',
  'origin',
  'poNumber',
  'productClaimCode',
  'ruling',
  'spi',
  'steelExclusionNumber',
  'steelRequirements',
  'tariffs',
  'taxOptions',
];

const PRODUCT_US_TARIFF_FIELDS: string[] = [
  'extraPgaFlags',
  'hts',
  'htsNumber',
  'pga',
  'pgaDescription',
  'unitAssist',
  'unitAssistPercentage',
  'unitReportingQuantities',
  'unitValue',
];

/* Narrow one (already schema-transformed) line to the Product input shape. */
const toProductEntryLine = (line: any): any => {
  const pruned: any = pick(line, PRODUCT_US_ENTRY_LINE_FIELDS);
  if (Array.isArray(line?.tariffs)) {
    pruned.tariffs = line.tariffs.map((tariff: any) =>
      pick(tariff, PRODUCT_US_TARIFF_FIELDS),
    );
  }
  return pruned;
};

type EntryLineTariff = { htsNumber?: string | null };
type EngineEntryLine = {
  tariffs?: EntryLineTariff[] | null;
  lines?: EngineEntryLine[] | null;
};

/* A real engine line carries fully classified tariffs (every tariff has an htsNumber). */
const isEngineLine = (line: EngineEntryLine | null | undefined): boolean => {
  const tariffs = line?.tariffs;
  return (
    Array.isArray(tariffs) &&
    tariffs.length > 0 &&
    tariffs.every((tariff) => Boolean(tariff?.htsNumber))
  );
};

const hasEngineLines = (
  lines: EngineEntryLine[] | null | undefined,
): lines is EngineEntryLine[] =>
  Array.isArray(lines) && lines.length > 0 && lines.every(isEngineLine);

const expandEngineLines = (entryLines: EngineEntryLine[]): EngineEntryLine[] =>
  entryLines.flatMap((entryLine) => {
    const { lines: engineLines, ...source } = entryLine ?? {};
    if (!hasEngineLines(engineLines)) {
      return [entryLine];
    }
    return engineLines.map((engineLine) => ({ ...source, ...engineLine }));
  });

/*
 * Map the engine's entry-line output to the Product input schema: expand split
 * lines, run the shared line transform (PGA/CPSC/etc., same as the entry save),
 * then narrow every line and tariff to the Product fields.
 */
const mapEntryLinesToProductSchema = (us: any): void => {
  if (!Array.isArray(us?.entryLines)) {
    transformLines.toSchema({ lines: us?.entryLines });
    return;
  }
  us.entryLines = expandEngineLines(us.entryLines);
  transformLines.toSchema({ lines: us.entryLines });
  us.entryLines = us.entryLines.map(toProductEntryLine);
};

export const transformProduct = {
  toForm: ({ existingRecord: newRecord }: { existingRecord: any }) => {
    transformLines.toForm({
      lines: newRecord.complianceDetails?.us?.entryLines,
    });
    return newRecord;
  },

  toCreateSchema: ({ input }: { input: any }) => {
    input.secondaryIdentifiers = input.secondaryIdentifiers?.filter(
      (si: ProductSecondaryIdentifier) => si.value,
    );
    mapEntryLinesToProductSchema(input.complianceDetails?.us);
    return input;
  },

  toUpdateSchema: ({ input }: { input: any }) => {
    input.secondaryIdentifiers = input.secondaryIdentifiers?.filter(
      (si: ProductSecondaryIdentifier) => si.value,
    );
    mapEntryLinesToProductSchema(input.complianceDetails?.us);
    delete input.shipper;
    return input;
  },
};
